import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Lock, Mail, Phone, User, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { setDocument } from '../../services/firebase'
import { useCollection } from '../../hooks/useFirestore'
import { normalizePhone } from '../../utils/helpers'
import { workerRegistrationSchema } from '../../utils/validators'
import { Button, Input, Checkbox } from '../../components/common'

const WorkersRegister = () => {
  const [submitError, setSubmitError] = useState(null)
  const { register: registerAccount } = useAuth()
  const navigate = useNavigate()

  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
    realtime: false,
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(workerRegistrationSchema),
    defaultValues: {
      displayName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      departmentInterests: [],
      smsConsent: false,
    },
  })

  const interests = watch('departmentInterests')
  const toggleInterest = (slug) => {
    const next = interests.includes(slug)
      ? interests.filter((s) => s !== slug)
      : [...interests, slug]
    setValue('departmentInterests', next)
  }

  const onSubmit = async (data) => {
    setSubmitError(null)
    const phone = normalizePhone(data.phone)
    if (!phone) {
      setSubmitError('Please enter a valid US phone number.')
      return
    }

    try {
      const credential = await registerAccount(data.email, data.password)
      await setDocument('users', credential.user.uid, {
        uid: credential.user.uid,
        email: data.email,
        displayName: data.displayName,
        phone,
        smsConsent: data.smsConsent,
        smsConsentAt: data.smsConsent ? new Date().toISOString() : null,
        departmentInterests: data.departmentInterests,
        role: 'worker',
        status: 'pending',
        isActive: true,
      })
      navigate('/workers', { replace: true })
    } catch (err) {
      setSubmitError(
        err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists. Try signing in instead.'
          : err.message
      )
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-dark to-secondary-900 flex items-center justify-center p-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/workers/login"
          className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="/images/logo.png"
              alt="DLBC Clarksville"
              className="w-20 h-20 object-contain mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold text-dark-100">
              Worker Registration
            </h1>
            <p className="text-gray-500 mt-2">
              Create your account — an admin will approve it before you can sign in
              to the portal.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              label="Full Name"
              icon={User}
              error={errors.displayName?.message}
              {...register('displayName')}
              placeholder="Jane Doe"
            />

            <Input
              label="Email Address"
              type="email"
              icon={Mail}
              error={errors.email?.message}
              {...register('email')}
              placeholder="you@example.com"
            />

            <Input
              label="Phone Number"
              type="tel"
              icon={Phone}
              error={errors.phone?.message}
              {...register('phone')}
              placeholder="(931) 555-0123"
            />

            <Input
              label="Password"
              type="password"
              icon={Lock}
              error={errors.password?.message}
              {...register('password')}
              placeholder="At least 8 characters"
            />

            <Input
              label="Confirm Password"
              type="password"
              icon={Lock}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
              placeholder="••••••••"
            />

            {(departments || []).length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Which departments do you serve in?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {departments.map((dept) => (
                    <Checkbox
                      key={dept.id}
                      label={dept.name}
                      checked={interests.includes(dept.id)}
                      onChange={() => toggleInterest(dept.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <Checkbox
                label="Text me service reminders"
                checked={watch('smsConsent')}
                onChange={(e) => setValue('smsConsent', e.target.checked)}
              />
              <p className="text-xs text-gray-500 mt-2">
                I agree to receive service reminder text messages from DLBC
                Clarksville at the number above. Message and data rates may apply.
                Reply STOP to opt out at any time.
              </p>
            </div>

            {submitError && <p className="text-red-500 text-sm">{submitError}</p>}

            <Button type="submit" variant="primary" fullWidth loading={isSubmitting}>
              Create Account
            </Button>

            <p className="text-gray-500 text-sm text-center">
              Already registered?{' '}
              <Link
                to="/workers/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

export default WorkersRegister
